/**
 * i18next initialization and configuration.
 *
 * Configures i18next with:
 * - react-i18next for React integration
 * - Tolgee-backed translation loading from the self-hosted Tolgee service
 * - HTTP backend fallback for local JSON files only when Tolgee credentials are absent
 * - Browser language detector for auto-detecting user's preferred language
 * - Normalization of Tolgee payloads back into i18next-compatible placeholders/plurals
 *
 * Translation files are organized by language and namespace:
 *   public/locales/{lng}/{ns}.json
 *
 * Set VITE_TOLGEE_ENABLED=true together with VITE_TOLGEE_API_URL and
 * VITE_TOLGEE_API_KEY to load translations directly from the Tolgee service.
 * JSON files remain the fallback only when browser Tolgee credentials are absent.
 *
 * @example
 * import '@/lib/i18n'; // Side-effect: initializes i18next
 * import { useTranslation } from 'react-i18next';
 *
 * function MyComponent() {
 *   const { t } = useTranslation();
 *   return <span>{t('common:buttons.save')}</span>;
 * }
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { parseTolgeeNamespacePayload } from './tolgee-payload';
import { resolveTolgeeRuntime } from './tolgee-runtime';

declare const __BUILD_TIMESTAMP__: string;

/**
 * Supported locales list. Must match the backend SUPPORTED_LOCALES constant.
 * Add new locales here when translation files are available.
 */
export const SUPPORTED_LOCALES = [
  'en-US',
  'de-DE',
  'pl-PL',
  'cs-CZ',
  'ro-RO',
  'bg-BG',
  'es-ES',
  'fr-FR',
  'nb-NO',
  'sv-SE',
  'it-IT',
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Simplified translate function signature compatible with i18next's TFunction.
 * Use this when passing `t` to helper functions that only need basic key lookup.
 */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Display names for supported locales, shown in native language.
 */
export const LOCALE_DISPLAY_NAMES: Record<SupportedLocale, string> = {
  'en-US': 'English',
  'de-DE': 'Deutsch',
  'pl-PL': 'Polski',
  'cs-CZ': 'Čeština',
  'ro-RO': 'Română',
  'bg-BG': 'Български',
  'es-ES': 'Español',
  'fr-FR': 'Français',
  'nb-NO': 'Norsk bokmål',
  'sv-SE': 'Svenska',
  'it-IT': 'Italiano',
};

/**
 * Map full locale codes to i18next language codes.
 * i18next uses short codes ('en', 'de') internally but we store
 * full BCP-47 codes ('en-US', 'de-DE') in the backend.
 */
export function localeToLanguage(locale: string): string {
  return locale.split('-')[0] ?? 'en';
}

/**
 * Map i18next language code to full locale code.
 */
export function languageToLocale(language: string): SupportedLocale {
  const match = SUPPORTED_LOCALES.find(
    (locale) => locale.startsWith(language) || localeToLanguage(locale) === language
  );
  return match ?? 'en-US';
}

/**
 * Tolgee-backed runtime is enabled only when the feature flag and API URL are
 * provided. Direct browser translation loading additionally requires an API key.
 */
const tolgeeEnabled = import.meta.env.VITE_TOLGEE_ENABLED === 'true';
const tolgeeApiUrl = import.meta.env.VITE_TOLGEE_API_URL;
const tolgeeApiKey = import.meta.env.VITE_TOLGEE_API_KEY;
const tolgeeEditorDefaultEnabled = import.meta.env.VITE_TOLGEE_EDITOR_DEFAULT === 'true';
const tolgeeRuntimeEnabled = tolgeeEnabled && !!tolgeeApiUrl;

function getTolgeeExtensionCredentials():
  | {
      apiKey: string;
      apiUrl: string;
    }
  | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const apiKey = sessionStorage.getItem('__tolgee_apiKey')?.trim();
    const apiUrl = sessionStorage.getItem('__tolgee_apiUrl')?.trim();

    if (!apiKey || !apiUrl) {
      return undefined;
    }

    return { apiKey, apiUrl };
  } catch {
    return undefined;
  }
}

const tolgeeRuntime = resolveTolgeeRuntime({
  enabled: tolgeeEnabled,
  apiUrl: tolgeeApiUrl,
  apiKey: tolgeeApiKey,
  isDev: import.meta.env.DEV,
  editorDefaultEnabled: tolgeeEditorDefaultEnabled,
  locationSearch: typeof window === 'undefined' ? '' : window.location.search,
  extensionCredentials: getTolgeeExtensionCredentials(),
});
const tolgeeBaseUrl = tolgeeRuntime.instanceApiUrl ?? tolgeeRuntime.baseUrl;
const tolgeeRequestApiKey = tolgeeRuntime.instanceApiKey;
const tolgeeBrowserApiEnabled = !!tolgeeBaseUrl && !!tolgeeRequestApiKey;
const tolgeeEditorModeEnabled = tolgeeRuntime.editorModeEnabled;

// Always load translations from local JSON files which are the single source
// of truth. Tolgee browser integration can be re-enabled once all namespaces
// are synced to the Tolgee service.
// Append build timestamp as query param to bust caches on each deploy.
const buildVersion = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : '';
const tolgeeBackendOptions = {
  loadPath: `/locales/{{lng}}/{{ns}}.json${buildVersion ? `?v=${buildVersion}` : ''}`,
};

const i18nInstance = i18n;
i18nInstance.use(HttpBackend);

i18nInstance.use(LanguageDetector);
i18nInstance.use(initReactI18next);

void i18nInstance.init({
  // Supported languages (short codes)
  supportedLngs: ['en', 'de', 'pl', 'cs', 'ro', 'bg', 'es', 'fr', 'nb', 'sv', 'it'],

  // Fallback to English when a key is missing
  fallbackLng: 'en',

  // Default namespace (loaded on startup)
  defaultNS: 'common',

  // Preload ALL namespaces at startup to avoid race conditions in production
  // where lazy-loaded namespace fetches can fail or arrive after first render,
  // causing raw translation keys to appear instead of translated text.
  ns: [
    'common',
    'admin',
    'ai',
    'analytics',
    'assets',
    'audit',
    'audit-workflow',
    'auditor-access',
    'bcp',
    'controls',
    'dashboard',
    'datahub',
    'esg',
    'evidence',
    'gaps',
    'ghg',
    'incidents',
    'nis2',
    'notifications',
    'policies',
    'portal',
    'risks',
    'settings',
    'suppliers',
    'surveys',
    'tasks',
    'vsme',
    'vulnerabilities',
  ],

  // Allow loading namespaces not listed in ns (enables lazy loading for any extras)
  partialBundledLanguages: true,

  // Interpolation config
  interpolation: {
    // React already escapes values
    escapeValue: false,
  },

  // Load from Tolgee's translations API whenever browser credentials are present.
  backend: tolgeeBackendOptions,

  // Language detector options
  detection: {
    // Detection order: localStorage first, then browser navigator
    order: ['localStorage', 'navigator'],
    // Cache detected language in localStorage
    caches: ['localStorage'],
    // localStorage key
    lookupLocalStorage: 'i18nextLng',
  },

  // React-specific options
  react: {
    // Use Suspense for loading translations (false = render fallback inline)
    useSuspense: false,
  },

  // Return empty string for missing keys in production, key name in dev
  returnEmptyString: false,
});

export { tolgeeEditorModeEnabled, tolgeeRuntimeEnabled };
export default i18n;
